<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAccountSelectOption',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    payload: {
      type: Object,
      required: true,
    },
    displayOwner: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const ownerCode = computed(() => {
    return props.payload.owner
      ? props.payload.owner.code
      : props.payload.owner_code;
  });

  watch(
    () => props.payload,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payload');
    }
  );
  watch(
    () => props.displayOwner,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('displayOwner');
    }
  );

  const act_7315092382398562_reloadComponentData_dn56n7yvi =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_o1c5qqicl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dn56n7yvi,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_o1c5qqicl],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_o1c5qqicl,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_o1c5qqicl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_o1c5qqicl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpText
      :muted="payload.inactive"
      :strikethrough="payload.inactive"
      elName="div"
    >
      <ItpIcon
        :name="
          payload.account_type === 'cash_book'
            ? $config.account.cashBook.icon
            : $config.account.bankAccount.icon
        "
        class="pe-1"
      >
      </ItpIcon>
      <strong class="pe-1" v-if="displayOwner && ownerCode">
        {{ ownerCode }} -
      </strong>
      <span class="text-truncate">
        <strong>{{ payload.name }}</strong> ({{ payload.currency }})
        {{ payload.bank || '' }}
      </span>
    </ItpText>
  </ItpUsercontrol>
</template>
